"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import { Typography } from "@repo/ui/aops-components/typography";
import type { CoursePageSection, FullCourse, FullCourseOffering, FullCourseOfferings } from "@repo/sanity-utils/query-types";
import { decorateAttributionAnchorClick, useAttributionHref } from "@repo/tracking/utm/lib/utm-attribution-client";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { getSecondaryCTAHeader, getSecondaryCTAScheduleLinks, resolveScheduleLinkTrackingEvent } from "./SecondaryCTAUtils";
import styles from "./SecondaryCTA.module.css";
interface SecondaryCTAProps {
  courseOfferings: FullCourseOfferings;
  offering: FullCourseOffering;
  courseSlug: string;
  campusIdForUrl: string | null;
  selectedCampusId: string | null;
  consultationUrl: string;
  hasPlacementTest: boolean;
  placementTestUrl: string;
  showBookConsultationButtons: boolean;
  coursePageSection?: NonNullable<CoursePageSection>;
  sequenceType?: NonNullable<FullCourse>["sequenceType"];
}
function SecondaryCtaLink(t0) {
  const $ = _c(13);
  const {
    href,
    label,
    slug,
    onTrackedClick
  } = t0;
  const decoratedHref = useAttributionHref(href || undefined);
  const t1 = `view-schedule-${slug}-button`;
  const t2 = decoratedHref ?? href;
  const t3 = !href;
  let t4;
  if ($[0] !== href || $[1] !== onTrackedClick) {
    t4 = event => {
      if (!href) {
        return;
      }
      decorateAttributionAnchorClick(event, href);
      onTrackedClick();
    };
    $[0] = href;
    $[1] = onTrackedClick;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== label) {
    t5 = <Typography variant="buttonMd" as="span">{label}</Typography>;
    $[3] = label;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className={styles.buttonSpacer} aria-hidden={true} />;
    t7 = <ArrowRight aria-hidden={true} size={20} strokeWidth={2.5} />;
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t8 = <a id={t1} href={t2} className={styles.button} target="_blank" rel="noopener noreferrer" aria-disabled={t3} onClick={t4}>{t5}{t6}{t7}</a>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}

/**
 * This is the bottom section of the CTA Card
 */
export function SecondaryCTA(t0) {
  const $ = _c(12);
  const {
    courseOfferings,
    offering,
    courseSlug,
    campusIdForUrl,
    selectedCampusId,
    consultationUrl,
    hasPlacementTest,
    placementTestUrl,
    showBookConsultationButtons,
    coursePageSection,
    sequenceType
  } = t0;
  let t1;
  if ($[0] !== campusIdForUrl || $[1] !== consultationUrl || $[2] !== courseOfferings || $[3] !== coursePageSection?.keyedCopy || $[4] !== courseSlug || $[5] !== hasPlacementTest || $[6] !== offering || $[7] !== placementTestUrl || $[8] !== selectedCampusId || $[9] !== sequenceType || $[10] !== showBookConsultationButtons) {
    const scheduleLinks = getSecondaryCTAScheduleLinks({
      courseOfferings,
      offering,
      campusIdForUrl,
      selectedCampusId,
      consultationUrl,
      hasPlacementTest,
      placementTestUrl,
      showBookConsultationButtons,
      keyedCopy: coursePageSection?.keyedCopy ?? [],
      sequenceType
    });
    const header = getSecondaryCTAHeader(scheduleLinks, coursePageSection?.keyedCopy ?? []);
    t1 = scheduleLinks.length > 0 && <div className={styles.secondaryCTA}><Typography as="p" variant="h5" className={styles.prompt}>{header}</Typography><div className={styles.buttons}>{scheduleLinks.map(t2 => {
          const {
            href,
            label,
            slug,
            offeringKey,
            trackSlug
          } = t2;
          return <SecondaryCtaLink key={slug} href={href} label={label} slug={slug} onTrackedClick={() => {
            if (!href) {
              return;
            }
            const trackingEvent = resolveScheduleLinkTrackingEvent(slug, courseSlug, offering, offering.modality.slug.current, selectedCampusId, offeringKey, trackSlug);
            if (trackingEvent) {
              sendDiscoveryEventBeacon(trackingEvent.event, trackingEvent.properties);
            }
          }} />;
        })}</div></div> || null;
    $[0] = campusIdForUrl;
    $[1] = consultationUrl;
    $[2] = courseOfferings;
    $[3] = coursePageSection?.keyedCopy;
    $[4] = courseSlug;
    $[5] = hasPlacementTest;
    $[6] = offering;
    $[7] = placementTestUrl;
    $[8] = selectedCampusId;
    $[9] = sequenceType;
    $[10] = showBookConsultationButtons;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}